import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import 'dayjs/locale/id';

dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);
dayjs.locale('id');

/** Semua pemformatan dalam bahasa dan kebiasaan Indonesia. */

export const formatNumber = (value) =>
  typeof value === 'number' ? value.toLocaleString('id-ID') : ' - ';

/**
 * Angka besar dipendekkan supaya kartu statistik tidak melebar tak terkendali.
 * 12.400 → "12,4 rb", 3.200.000 → "3,2 jt".
 */
export const formatCompact = (value) => {
  if (typeof value !== 'number') return ' - ';
  if (value < 1_000) return String(value);
  if (value < 1_000_000) return `${(value / 1_000).toFixed(1).replace('.', ',').replace(',0', '')} rb`;
  return `${(value / 1_000_000).toFixed(1).replace('.', ',').replace(',0', '')} jt`;
};

export const formatIdr = (value) =>
  typeof value === 'number'
    ? new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        maximumFractionDigits: 0,
      }).format(value)
    : ' - ';

export const formatBytes = (bytes) => {
  if (typeof bytes !== 'number' || bytes < 0) return ' - ';
  if (bytes === 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const index = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / 1024 ** index;

  return `${value.toFixed(index === 0 ? 0 : 1).replace('.', ',')} ${units[index]}`;
};

/** "2 jam lalu", "kemarin" - sesuai gaya penulisan di rancangan. */
export const formatRelative = (date) => {
  if (!date) return ' - ';
  const target = dayjs(date);
  if (!target.isValid()) return ' - ';

  const diffHours = dayjs().diff(target, 'hour');
  // Untuk yang lebih dari seminggu, tanggal pasti lebih informatif daripada
  // "8 hari lalu".
  if (Math.abs(diffHours) > 24 * 7) return target.format('D MMM YYYY');
  return target.fromNow();
};

export const formatDateTime = (date) =>
  date && dayjs(date).isValid() ? dayjs(date).format('D MMM YYYY, HH:mm') : ' - ';

export const formatDate = (date) =>
  date && dayjs(date).isValid() ? dayjs(date).format('D MMMM YYYY') : ' - ';

/** Durasi dalam bentuk yang mudah dibaca: "1,4 dtk", "2 mnt 30 dtk". */
export const formatDuration = (ms) => {
  if (typeof ms !== 'number' || ms < 0) return ' - ';
  if (ms < 1_000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1).replace('.', ',')} dtk`;

  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.round((ms % 60_000) / 1000);
  if (minutes < 60) return seconds ? `${minutes} mnt ${seconds} dtk` : `${minutes} mnt`;

  const hours = Math.floor(minutes / 60);
  return `${hours} jam ${minutes % 60} mnt`;
};

/** Membuang skema dan "www." supaya domain enak dibaca di tabel. */
export const formatDomain = (url) => {
  if (!url) return ' - ';
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
  }
};

/** Memotong teks panjang dengan elipsis, memakai karakter tunggal "…". */
export const truncate = (text, max = 60) => {
  if (typeof text !== 'string') return '';
  return text.length <= max ? text : `${text.slice(0, max - 1).trimEnd()}…`;
};

export const initials = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || '?';
